import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

// Slow, elegant fade-in when the element scrolls into view
const Reveal = ({ children, className = "", delay = 0 }) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setVisible(true);
        });
      },
      { threshold: 0.35 },
    );
    obs.observe(node);
    return () => obs.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      style={{
        transitionDelay: `${delay}ms`,
      }}
      className={`transition-all duration-[1400ms] ease-out ${
        visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
      } ${className}`}
    >
      {children}
    </div>
  );
};

// A single "spread" — full viewport centered content, dark background
const Spread = ({ children, tone = "ink", testid }) => {
  const bg =
    tone === "ink"
      ? "bg-paula-ink text-white"
      : tone === "cream"
        ? "bg-paula-cream text-paula-ink"
        : "bg-white text-paula-ink";
  return (
    <section
      data-testid={testid}
      className={`min-h-screen w-full flex items-center justify-center px-6 py-24 ${bg}`}
    >
      <div className="max-w-2xl w-full">{children}</div>
    </section>
  );
};

// A pure "white/blank" pause page — no content, just a visual breath
const BlankPage = ({ tone = "white", testid, label }) => {
  const bg =
    tone === "white" ? "bg-white" : tone === "cream" ? "bg-paula-cream" : "bg-paula-ink";
  return (
    <section
      data-testid={testid}
      className={`min-h-[60vh] w-full flex items-center justify-center ${bg}`}
    >
      {label && (
        <span
          className={`text-xs uppercase tracking-[0.3em] ${
            tone === "ink" ? "text-white/30" : "text-paula-ink/25"
          }`}
        >
          {label}
        </span>
      )}
    </section>
  );
};

const pTeam = [
  {
    name: "Paula",
    role: "Rettungssanitäterin",
    img: "https://customer-assets.emergentagent.com/job_notfall-werkstatt/artifacts/vxyrztvr_file_000000007b4071f6bc9cccab648b6dcc.png",
  },
  {
    name: "Paul",
    role: "Feuerwehr",
    img: "https://customer-assets.emergentagent.com/job_notfall-werkstatt/artifacts/ufctngs1_openart-22b5188a05b54bba9805e42426b3ef94_00001__raw.png",
  },
  {
    name: "Peter",
    role: "Polizist",
    img: "https://customer-assets.emergentagent.com/job_notfall-werkstatt/artifacts/se681tjl_file_00000000f71c7246b166c850c68e4556_edit_285000589227865.jpg",
  },
  {
    name: "Pia",
    role: "Bevölkerungsschutz",
    img: "https://customer-assets.emergentagent.com/job_notfall-werkstatt/artifacts/8g2knlmo_file_00000000b480720aa662f07fd818b403.png",
  },
  {
    name: "Piet",
    role: "Luftrettung",
    img: "https://customer-assets.emergentagent.com/job_notfall-werkstatt/artifacts/4y8fqhr6_Screenshot_20260530_154910_com.openai.chatgpt.jpg",
  },
];

export default function FuerDieJury() {
  useEffect(() => {
    window.scrollTo(0, 0);
    const prev = document.title;
    document.title = "Für die Jury · Pinguin Paula";
    return () => {
      document.title = prev;
    };
  }, []);

  const line = "font-display leading-[1.15] tracking-tight";

  return (
    <div data-testid="jury-page" className="bg-paula-ink">
      {/* Cover */}
      <Spread tone="ink" testid="spread-cover">
        <div className="text-center">
          <Reveal>
            <h1 className={`${line} text-5xl sm:text-6xl lg:text-7xl font-bold`}>
              Hilfe kennt keine Grenzen.
            </h1>
          </Reveal>
          <Reveal delay={800}>
            <p
              className={`${line} text-4xl sm:text-5xl lg:text-6xl font-bold text-paula-yellow mt-8`}
            >
              Kinder auch nicht.
            </p>
          </Reveal>
          <Reveal delay={2000}>
            <p className="mt-16 text-xs uppercase tracking-[0.4em] text-white/40">
              Ein Brief an die Jury
            </p>
          </Reveal>
        </div>
      </Spread>

      {/* White page */}
      <BlankPage tone="white" testid="spread-blank-1" label="weiße Seite" />

      {/* Anrede */}
      <Spread tone="ink" testid="spread-anrede">
        <div className="space-y-6 text-2xl sm:text-3xl">
          <Reveal>
            <p className={`${line} font-semibold`}>Liebe Jury,</p>
          </Reveal>
          <Reveal delay={300}>
            <p className={line}>bevor Sie weiterlesen…</p>
          </Reveal>
          <Reveal delay={600}>
            <p className={line}>möchte ich Sie um etwas bitten.</p>
          </Reveal>
          <Reveal delay={900}>
            <p className={line}>
              Legen Sie für einen kurzen Moment Ihren Beruf ab.
            </p>
          </Reveal>
          <Reveal delay={1200}>
            <p className={line}>Vergessen Sie Kriterien.</p>
          </Reveal>
          <Reveal delay={1400}>
            <p className={line}>Vergessen Sie Punkte.</p>
          </Reveal>
          <Reveal delay={1600}>
            <p className={line}>Vergessen Sie Bewertungen.</p>
          </Reveal>
          <Reveal delay={2000}>
            <p className="text-white/40 text-xl">…</p>
          </Reveal>
          <Reveal delay={2300}>
            <p className={`${line} text-paula-yellow font-semibold text-3xl sm:text-4xl`}>
              …und werden Sie noch einmal sechs Jahre alt.
            </p>
          </Reveal>
        </div>
      </Spread>

      {/* Umblättern */}
      <BlankPage tone="cream" testid="spread-turn-1" label="(umblättern)" />

      {/* Paula speaks */}
      <Spread tone="ink" testid="spread-paula-hello">
        <div className="space-y-6 text-2xl sm:text-3xl">
          <Reveal>
            <div className="text-6xl mb-4">🐧</div>
          </Reveal>
          <Reveal delay={300}>
            <p className={`${line} text-4xl sm:text-5xl font-bold`}>Hallo!</p>
          </Reveal>
          <Reveal delay={600}>
            <p className={line}>Ich bin Paula.</p>
          </Reveal>
          <Reveal delay={900}>
            <p className={`${line} text-white/70`}>Oder vielleicht Paul.</p>
          </Reveal>
          <Reveal delay={1050}>
            <p className={`${line} text-white/70`}>Oder Peter.</p>
          </Reveal>
          <Reveal delay={1200}>
            <p className={`${line} text-white/70`}>Oder Pia.</p>
          </Reveal>
          <Reveal delay={1350}>
            <p className={`${line} text-white/70`}>Oder Piet.</p>
          </Reveal>
          <Reveal delay={1700}>
            <p className={line}>Eigentlich ist das gar nicht so wichtig.</p>
          </Reveal>
          <Reveal delay={2100}>
            <p className={line}>Viel wichtiger ist…</p>
          </Reveal>
          <Reveal delay={2500}>
            <p
              className={`${line} text-paula-yellow font-semibold text-3xl sm:text-4xl`}
            >
              …dass du heute entdecken darfst,
              <br />
              dass Helfen jeder lernen kann.
            </p>
          </Reveal>
        </div>
      </Spread>

      {/* Sirens */}
      <Spread tone="ink" testid="spread-sirens">
        <div className="space-y-6 text-2xl sm:text-3xl">
          <Reveal>
            <p className={line}>Kennst du das?</p>
          </Reveal>
          <Reveal delay={400}>
            <p className={line}>Du hörst eine Sirene…</p>
          </Reveal>
          <Reveal delay={900}>
            <div className="flex gap-6 text-6xl sm:text-7xl my-6">
              <span>🚒</span>
              <span>🚑</span>
              <span>🚓</span>
            </div>
          </Reveal>
          <Reveal delay={1400}>
            <p className={line}>…und fragst dich:</p>
          </Reveal>
          <Reveal delay={1700}>
            <p className={`${line} italic text-white/80`}>
              „Was passiert da gerade?"
            </p>
          </Reveal>
          <Reveal delay={2200}>
            <p className={`${line} text-paula-yellow font-semibold`}>Ich auch.</p>
          </Reveal>
        </div>
      </Spread>

      {/* Discovery */}
      <Spread tone="ink" testid="spread-discovery">
        <div className="space-y-5 text-2xl sm:text-3xl">
          <Reveal>
            <p className={line}>Deshalb gehen wir einfach mit.</p>
          </Reveal>
          <Reveal delay={300}>
            <p className={line}>Wir schauen hinter Türen.</p>
          </Reveal>
          <Reveal delay={600}>
            <p className={line}>Wir stellen Fragen.</p>
          </Reveal>
          <Reveal delay={900}>
            <p className={line}>Wir dürfen neugierig sein.</p>
          </Reveal>
          <Reveal delay={1500}>
            <p className={`${line} text-paula-yellow font-semibold mt-10`}>
              Denn Neugier ist der Anfang von Mut.
            </p>
          </Reveal>
        </div>
      </Spread>

      {/* Small acts of helping */}
      <Spread tone="ink" testid="spread-helping-begins">
        <div className="space-y-5 text-2xl sm:text-3xl">
          <Reveal>
            <p className={line}>Und weißt du, was wir entdeckt haben?</p>
          </Reveal>
          <Reveal delay={700}>
            <p className={line}>Helfen beginnt nicht erst,</p>
          </Reveal>
          <Reveal delay={900}>
            <p className={line}>wenn Blaulicht an ist.</p>
          </Reveal>
          <Reveal delay={1400}>
            <p className={`${line} text-paula-yellow font-semibold`}>
              Es beginnt viel früher.
            </p>
          </Reveal>
          <Reveal delay={1900}>
            <ul className="mt-8 space-y-3 text-white/85 text-xl sm:text-2xl">
              <li>Mit Hinsehen.</li>
              <li>Mit Dableiben.</li>
              <li>Mit Zuhören.</li>
              <li>Mit Vertrauen.</li>
              <li>Mit einem Notruf.</li>
              <li>Mit einer offenen Tür.</li>
              <li>Mit einem Pflaster.</li>
              <li>Mit einer Umarmung.</li>
              <li className="pt-4 text-white font-semibold">
                Mit einem Kind, das keine Angst hat zu helfen.
              </li>
            </ul>
          </Reveal>
        </div>
      </Spread>

      {/* The P-Team */}
      <Spread tone="ink" testid="spread-pteam">
        <div className="w-full">
          <Reveal>
            <p className="text-2xl sm:text-3xl font-display">Deshalb gibt es uns.</p>
          </Reveal>
          <Reveal delay={400}>
            <div className="mt-4 space-y-1 text-white/60 text-xl">
              <p>Nicht nur Paula.</p>
              <p>Nicht nur Paul.</p>
              <p>Nicht nur Peter.</p>
              <p>Nicht nur Pia.</p>
              <p>Nicht nur Piet.</p>
            </div>
          </Reveal>
          <Reveal delay={1200}>
            <p className="mt-6 text-2xl sm:text-3xl font-display">Sondern…</p>
          </Reveal>
          <Reveal delay={1600}>
            <p className="mt-4 text-5xl sm:text-6xl font-display font-bold text-paula-yellow">
              das P-Team.
            </p>
          </Reveal>

          <Reveal delay={2100}>
            <div className="mt-12 grid grid-cols-5 gap-3">
              {pTeam.map((p) => (
                <div key={p.name} className="text-center">
                  <div className="aspect-square rounded-2xl bg-white/5 border border-white/10 overflow-hidden mb-2">
                    <img
                      src={p.img}
                      alt={`Pinguin ${p.name}`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="text-xs sm:text-sm font-display font-semibold">
                    {p.name}
                  </div>
                  <div className="text-[10px] sm:text-xs text-white/50 leading-tight">
                    {p.role}
                  </div>
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal delay={2500}>
            <div className="mt-12 space-y-2 text-xl sm:text-2xl text-white/85">
              <p>Fünf Freunde.</p>
              <p>Fünf Themen.</p>
              <p>Eine Idee.</p>
            </div>
          </Reveal>
          <Reveal delay={3000}>
            <p className="mt-8 text-2xl sm:text-3xl font-display italic text-white/70">
              Hilfe kennt keine Grenzen.
            </p>
          </Reveal>
        </div>
      </Spread>

      {/* Belief setup */}
      <Spread tone="ink" testid="spread-belief">
        <div className="space-y-5 text-2xl sm:text-3xl">
          <Reveal>
            <p className={line}>Und deshalb glauben wir etwas.</p>
          </Reveal>
          <Reveal delay={500}>
            <p className={`${line} text-white/70`}>
              Etwas, das man nicht in einem Schulbuch findet.
            </p>
          </Reveal>
          <Reveal delay={800}>
            <p className={`${line} text-white/70`}>
              Nicht in einer Dienstvorschrift.
            </p>
          </Reveal>
          <Reveal delay={1100}>
            <p className={`${line} text-white/70`}>Nicht in einer Checkliste.</p>
          </Reveal>
          <Reveal delay={1600}>
            <p className={`${line} text-paula-yellow font-semibold`}>
              Sondern mitten im Leben.
            </p>
          </Reveal>
        </div>
      </Spread>

      {/* THE CORE SENTENCE — its own moment */}
      <Spread tone="ink" testid="spread-core">
        <div className="text-center">
          <Reveal>
            <div className="w-16 h-0.5 bg-paula-yellow mx-auto mb-10" />
          </Reveal>
          <Reveal delay={500}>
            <p
              className={`${line} text-3xl sm:text-4xl lg:text-5xl font-bold`}
            >
              Kinder sind kein schwaches Glied
              <br />
              der Rettungskette.
            </p>
          </Reveal>
          <Reveal delay={1800}>
            <p
              className={`${line} text-3xl sm:text-4xl lg:text-5xl font-bold text-paula-yellow mt-8`}
            >
              Sie sind ihr bisher unsichtbares Glied.
            </p>
          </Reveal>
          <Reveal delay={3200}>
            <div className="w-16 h-0.5 bg-paula-yellow mx-auto mt-10" />
          </Reveal>
        </div>
      </Spread>

      {/* Blank pause */}
      <BlankPage tone="white" testid="spread-blank-2" label="weiße Seite" />

      {/* Closing — give the jury back their role */}
      <Spread tone="ink" testid="spread-closing">
        <div className="space-y-6 text-2xl sm:text-3xl">
          <Reveal>
            <p className={`${line} font-semibold`}>Liebe Jury,</p>
          </Reveal>
          <Reveal delay={300}>
            <p className={line}>
              Sie dürfen jetzt Ihren Beruf wieder anziehen.
            </p>
          </Reveal>
          <Reveal delay={700}>
            <p className={`${line} text-white/80`}>Ihre Erfahrung.</p>
          </Reveal>
          <Reveal delay={900}>
            <p className={`${line} text-white/80`}>Ihr Wissen.</p>
          </Reveal>
          <Reveal delay={1100}>
            <p className={`${line} text-white/80`}>Ihre Verantwortung.</p>
          </Reveal>
          <Reveal delay={1500}>
            <p className={line}>Aber vielleicht…</p>
          </Reveal>
          <Reveal delay={1900}>
            <p className={`${line} text-paula-yellow font-semibold`}>
              …mit einem kleinen Gedanken mehr.
            </p>
          </Reveal>
          <Reveal delay={2600}>
            <div className="pt-10 border-t border-white/10 mt-10">
              <p className={`${line} text-2xl sm:text-3xl`}>
                Was wäre möglich,
                <br />
                wenn wir Kinder nicht erst dann ernst nehmen,
                <br />
                wenn etwas passiert…
              </p>
            </div>
          </Reveal>
          <Reveal delay={3400}>
            <p
              className={`${line} text-3xl sm:text-4xl font-bold text-paula-yellow`}
            >
              …sondern schon lange davor?
            </p>
          </Reveal>
        </div>
      </Spread>

      {/* Signature */}
      <Spread tone="ink" testid="spread-signature">
        <div className="text-center">
          <Reveal>
            <p className="text-sm uppercase tracking-[0.3em] text-white/40 mb-6">
              Mit Herz und Notruf
            </p>
          </Reveal>
          <Reveal delay={400}>
            <p className="font-display text-3xl sm:text-4xl font-bold">
              Simone Faller
            </p>
          </Reveal>
          <Reveal delay={700}>
            <p className="text-white/50 text-sm mt-2">
              & das P-Team · Solingen
            </p>
          </Reveal>
          <Reveal delay={1400}>
            <div className="mt-16 flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/"
                data-testid="jury-back-home"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-white/20 text-white hover:bg-white/10 transition-colors text-sm"
              >
                <ArrowLeft size={16} />
                Zur Landing Page
              </Link>
              <a
                href="https://notfall-werkstatt.emergent.host"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="jury-open-app"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-paula-yellow text-paula-ink font-semibold text-sm hover:bg-white transition-colors"
              >
                Die Notfall-Werkstatt öffnen →
              </a>
            </div>
          </Reveal>
        </div>
      </Spread>
    </div>
  );
}
